import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { IQuoteableCarriersProduct } from '@boldpenguin/sdk-types';
import { LogLevel, LoggerAbstractService } from 'bqx-logging';
import { NGXLogger } from 'ngx-logger';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ApplicationFormState, ApplicationQuestionSetCodes } from 'src/app/enums/sdk-application.enum';
import { IQuestionSet } from 'src/app/models/application-form/application-form.model';
import { ApplicationFormAbstractService } from 'src/app/services/application-form/application-form.abstract.service';
import { InputFocusAbstractService } from 'src/app/services/input-focus/input-focus.abstract.service';

@Component({
  selector: 'appform-question-set-navigation',
  templateUrl: './question-set-navigation.component.html',
  styleUrls: ['./question-set-navigation.component.css']
})
export class QuestionSetNavigationComponent implements OnInit, OnChanges {
  private _inputsOnDomUnsub$ = new Subject<void>();
  private _applicationUnsub$ = new Subject<void>();
  private _applicationStateUnsub$ = new Subject<void>();
  private _quoteReqUnsub$ = new Subject<void>();
  private _carrierProductsUnsub$ = new Subject<void>();
  private _appFormState: string;
  private _questionSets: IQuestionSet[];
  private _totalQuotes: number;
  private _totalOfflineQuotes: number;
  private _carrierProducts: IQuoteableCarriersProduct[];

  @Input() activeMenuOrder: number;
  @Input() activeQuestionSet: IQuestionSet;
  @Output() menuOrderChange = new EventEmitter<number>();
  public isAppSyncedWithBP$ = new Subject<boolean>();
  public isActiveQuestionSetLoading$ = new Subject<boolean>();
  public areQuotesLoading$ = new Subject<boolean>();

  public get questionSets(): IQuestionSet[] {
    return this._questionSets?.length > 0
      ? this._questionSets.filter((_que) => {
          //TODO: SDK 11 will element this extra step
          if (_que.is_blank === false && _que.parentQuestionSetId !== null) {
            const onlyAnchors = _que.answers.every((ans) => ans.question.field_type === 'anchor');
            if (onlyAnchors) {
              return (
                _que.answers.find((ans) => _que.question_set.question_pools.find((p) => p.parent_question_id === ans.question.id)) !==
                undefined
              );
            }
          }
          return _que.is_blank === false;
        })
      : [];
  }

  public get appFormState(): string {
    return this._appFormState;
  }

  public get isQuoted(): boolean {
    return this._appFormState === ApplicationFormState.QUOTED;
  }

  public get isCarrierPageEnabled(): boolean {
    return (
      (this.isQuoted ||
        (!this.consentQuestionSet && this._appFormState === ApplicationFormState.COMPLETED) ||
        this.applicationFormService.isReferred) &&
      this.applicationFormService.readOnlyMode$.value
    );
  }

  public get isBusinessInfoMenuActive(): boolean {
    return this.activeMenuOrder <= 3;
  }

  public get showCheckMarkForBusinessInfo(): boolean {
    return !this.showCheckMark(1) || !this.showCheckMark(2) || !this.showCheckMark(3);
  }

  public get totalCarrierProducts(): number {
    if (this.isQuoted) return (this._totalQuotes ? this._totalQuotes : 0) + this.offlineQuotesCount();
    else return this.offlineQuotesCount();
  }

  public get consentQuestionSet(): IQuestionSet | null {
    const questionSets = this._questionSets?.filter((queSet) => queSet.question_set.code === ApplicationQuestionSetCodes.CONSENT);
    return questionSets ? questionSets[0] : null;
  }

  constructor(
    private inputFocusService: InputFocusAbstractService,
    private applicationFormService: ApplicationFormAbstractService,
    private loggerService: LoggerAbstractService,
    private logger: NGXLogger
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (Object.keys(changes).some((k) => k === 'activeMenuOrder')) {
      if (changes?.activeMenuOrder?.currentValue === 1) {
        this.setFocusByQuestionCode('progressive_commercial_first_name', false);
      } else if (changes?.activeMenuOrder?.currentValue === 2) {
        this.setFocusByQuestionCode('progressive_commercial_coverage_types', false);
      }
    }
  }

  ngOnInit(): void {
    this.isAppSyncedWithBP$ = this.applicationFormService.isAppSyncedWithBP$;
    this.isActiveQuestionSetLoading$ = this.applicationFormService.isActiveQuestionSetLoading$;
    this.areQuotesLoading$ = this.applicationFormService.areQuotesLoading$;
    this.applicationFormService.application$.pipe(takeUntil(this._applicationUnsub$)).subscribe(
      (formData) => {
        this._questionSets = formData?.questionSets.list;
      },
      (err: string) => {
        this.loggerService.postLogData(LogLevel.Error, 'question-set-navigation: ngOnInit questionSets', err, '');
        this._applicationUnsub$.next();
        this._applicationUnsub$.complete();
      }
    );

    this.applicationFormService.appFormState$.pipe(takeUntil(this._applicationStateUnsub$)).subscribe(
      (state) => {
        this._appFormState = state;
        if (
          this._appFormState === ApplicationFormState.QUOTED ||
          this._appFormState === ApplicationFormState.COMPLETED ||
          this.applicationFormService.isReferred
        ) {
          this.activeMenuOrder = 0;
          if (this.consentQuestionSet) {
            if (this.consentQuestionSet.is_completed === false) {
              this.activeMenuOrder = this.consentQuestionSet.order;
            }
          }
          this.menuOrderChange.emit(this.activeMenuOrder);
        }
      },
      (err: string) => {
        this.loggerService.postLogData(LogLevel.Error, 'question-set-navigation: ngOnInit appFormState$', err, '');
        this._applicationStateUnsub$.next();
        this._applicationStateUnsub$.complete();
      }
    );

    this.applicationFormService.quoteRequests$.pipe(takeUntil(this._quoteReqUnsub$)).subscribe(
      (quotes) => {
        if (quotes) this._totalQuotes = quotes.length;
        else this._totalQuotes = 0;
      },
      (err: string) => {
        this.loggerService.postLogData(LogLevel.Error, 'question-set-navigation: ngOnInit quoteRequests$', err, '');
        this._quoteReqUnsub$.next();
        this._quoteReqUnsub$.complete();
      }
    );

    this.applicationFormService.carrierProducts$.pipe(takeUntil(this._carrierProductsUnsub$)).subscribe(
      (carrierProducts) => {
        this._carrierProducts = carrierProducts;
      },
      (err: string) => {
        this.loggerService.postLogData(LogLevel.Error, 'question-set-navigation: ngOnInit quoteRequests$', err, '');
        this._carrierProductsUnsub$.next();
        this._carrierProductsUnsub$.complete();
      }
    );

    this.subscribeControlToFocus();
  }

  public setActiveMenuItem(menuId: string): void {
    switch (menuId) {
      case 'quote_info_menu': {
        this.applicationFormService.changeActiveQuestionSet(0);
        this.activeMenuOrder = 0;
        this.menuOrderChange.emit(this.activeMenuOrder);
        break;
      }
      case 'business_info_menu': {
        this.applicationFormService.changeActiveQuestionSet(1);
        this.activeMenuOrder = 1;
        this.menuOrderChange.emit(this.activeMenuOrder);
        break;
      }
      case 'general_info_menu': {
        this.applicationFormService.changeActiveQuestionSet(4);
        this.activeMenuOrder = 4;
        this.menuOrderChange.emit(this.activeMenuOrder);
        break;
      }
      default: {
        const order = this.questionSets?.find((set) => set.question_set.code === menuId).order;
        const questSetid = this.questionSets?.find((set) => set.question_set.code === menuId).id;

        this.applicationFormService.changeActiveQuestionSet(order, questSetid);
        this.activeMenuOrder = order;
        this.menuOrderChange.emit(this.activeMenuOrder);
        break;
      }
    }
  }

  private offlineQuotesCount(): number {
    this._totalOfflineQuotes = 0;
    if (this._carrierProducts)
      if (this._carrierProducts.filter((carrier) => carrier.carrier.online).length <= 0)
        this._totalOfflineQuotes = this._carrierProducts.length;
      else
        this._totalOfflineQuotes = this._carrierProducts.filter(
          (carrier) => carrier.carrier.online === false && !this.isNonAdmittedCarrier(carrier)
        )?.length;

    return this._totalOfflineQuotes;
  }

  private isNonAdmittedCarrier(carrierProduct: IQuoteableCarriersProduct): boolean {
    if (sessionStorage.getItem('EOM') !== 'Y' && this._appFormState === ApplicationFormState.QUOTED) {
      if (carrierProduct.product.name === 'General Liability' && carrierProduct.carrier.name.indexOf('Evanston') > -1) return true;
    }
    return false;
  }
  private subscribeControlToFocus() {
    this.inputFocusService.resetTotalInputsBuilt();
    this.inputFocusService.agentInputsOnDom$.isStopped = false;
    this.inputFocusService.agentInputsOnDom$.pipe(takeUntil(this._inputsOnDomUnsub$)).subscribe(
      (loadedInputs: number) => {
        if (loadedInputs > 0 && this.activeMenuOrder === 1) {
          this.setFocusByQuestionCode('progressive_commercial_first_name', false);
        } else if (loadedInputs > 0 && this.activeMenuOrder === 2) {
          this.setFocusByQuestionCode('progressive_commercial_coverage_types', false);
        }
      },
      (err: string) => {
        this.loggerService.postLogData(LogLevel.Error, 'question-set-navigation: ngOnInit subscribeControlToFocus', err, '');
        this._inputsOnDomUnsub$.next();
        this._inputsOnDomUnsub$.complete();
      }
    );
  }

  private setFocusByQuestionCode(code: string | undefined, scroll = true) {
    if (code) {
      const question = this.applicationFormService.getQuestionByCode(code);
      const el: HTMLElement | null = document.getElementById(question.id);
      if (el) {
        this.setFocus(el, scroll);
      }
    }
  }

  private setFocus(el: HTMLElement, scroll: boolean, firstAttempt = true) {
    // select input focuses on mat-select rather than an input element
    // so a search for that input is performed first by searching for
    // class name "focusInput"
    const elClasses = el.className.split(' ');
    if (el && elClasses.includes('focusInput') && el instanceof HTMLElement) {
      el.focus();
    }
    // if focusInput class was not found, that means select is not the first
    // element. so we can find the first input tag in the whole document,
    // confident that it will be the first input
    else {
      const p = document.getElementsByTagName('input')[0];
      if (p instanceof HTMLElement) {
        p.focus();
      }
    }
    if (scroll) {
      (el as HTMLElement).scrollIntoView({ behavior: 'smooth' });
    }
  }

  //determines whether the check should be shown for navigation panels
  public showCheckMark(qSet: number, qCode?: string): boolean | undefined {
    if (this._questionSets?.length > 0)
      return qCode
        ? this._questionSets?.filter((ques) => ques.order === qSet && ques.question_set.code === qCode && ques.is_blank === false)[0]
            ?.is_completed
        : this._questionSets?.filter((ques) => ques.order === qSet && ques.is_blank === false)[0]?.is_completed;
    else return false;
  }

  //This method is only MQS Optimization page tittle display
  public getMQSPageTitle(org_title: string, code: string): string {
    return code.includes('mqs') ? 'MQS-' + org_title : org_title;
  }
}
